import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { firstValueFrom, timeout } from 'rxjs';

import { environment } from '@environment';

export type H5PContentNode =
  | string
  | number
  | boolean
  | null
  | H5PContentNode[]
  | { [key: string]: H5PContentNode };

const MEDIA_MIME_PREFIXES = ['video/', 'audio/'] as const;
const SIGNED_URL_TIMEOUT_MS = 5000;
const TMP_PATH_SUFFIX = '#tmp';
const NO_REDIRECT_PATH = '/content-no-redirect/';
const HAS_PROTOCOL = /^https?:\/\//i;

function isRecord(
  node: H5PContentNode,
): node is { [key: string]: H5PContentNode } {
  return typeof node === 'object' && node !== null && !Array.isArray(node);
}

// Android fetches media in a separate process that sends no cookies, so the
// cookie-authenticated content route 500s. Presigned URLs need no auth.
@Injectable({ providedIn: 'root' })
export class H5pMediaUrlService {
  private readonly http = inject(HttpClient);

  public async resolveMediaUrls<T extends H5PContentNode>(
    content: T,
    contentId: string,
  ): Promise<T> {
    if (!content || !contentId) {
      return content;
    }

    const paths = new Set<string>();
    this.collectMediaPaths(content, paths);
    if (!paths.size) {
      return content;
    }

    const resolved = await Promise.all(
      [...paths].map(
        async path =>
          [path, await this.fetchSignedUrl(contentId, path)] as const,
      ),
    );

    const signedUrls = new Map<string, string>();
    resolved.forEach(([path, url]) => {
      if (url) {
        signedUrls.set(path, url);
      }
    });

    return signedUrls.size
      ? (this.replaceMediaPaths(content, signedUrls) as T)
      : content;
  }

  private collectMediaPaths(node: H5PContentNode, paths: Set<string>): void {
    if (Array.isArray(node)) {
      node.forEach(child => this.collectMediaPaths(child, paths));
      return;
    }
    if (!isRecord(node)) {
      return;
    }

    const path = this.resolvablePathOf(node);
    if (path) {
      paths.add(path);
    }
    Object.values(node).forEach(child => this.collectMediaPaths(child, paths));
  }

  private replaceMediaPaths(
    node: H5PContentNode,
    signedUrls: Map<string, string>,
  ): H5PContentNode {
    if (Array.isArray(node)) {
      return node.map(child => this.replaceMediaPaths(child, signedUrls));
    }
    if (!isRecord(node)) {
      return node;
    }

    const next: { [key: string]: H5PContentNode } = {};
    Object.entries(node).forEach(([key, value]) => {
      next[key] = this.replaceMediaPaths(value, signedUrls);
    });

    const path = this.resolvablePathOf(node);
    const signedUrl = path && signedUrls.get(path);
    if (signedUrl) {
      next['path'] = signedUrl;
    }
    return next;
  }

  private resolvablePathOf(node: {
    [key: string]: H5PContentNode;
  }): string | null {
    const { path, mime } = node;
    if (typeof path !== 'string' || !path || typeof mime !== 'string') {
      return null;
    }
    if (!MEDIA_MIME_PREFIXES.some(prefix => mime.startsWith(prefix))) {
      return null;
    }
    if (HAS_PROTOCOL.test(path) || path.endsWith(TMP_PATH_SUFFIX)) {
      return null;
    }
    return path;
  }

  private async fetchSignedUrl(
    contentId: string,
    path: string,
  ): Promise<string | null> {
    const encodedPath = path
      .split('/')
      .map(segment => encodeURIComponent(segment))
      .join('/');

    try {
      const signedUrl = await firstValueFrom(
        this.http
          .get(
            `${environment.h5pBaseUrl}${NO_REDIRECT_PATH}${contentId}/${encodedPath}`,
            { responseType: 'json' },
          )
          .pipe(timeout(SIGNED_URL_TIMEOUT_MS)),
      );
      return typeof signedUrl === 'string' && signedUrl ? signedUrl : null;
    } catch {
      return null;
    }
  }
}
